import { MAP_LINK_HOSTS, parseMapLink } from './map-link.js';
/**
 * Krok GPS1 — ROZBALENI KRATKEHO ODKAZU.
 *
 * `goo.gl/abcd` a `mapy.cz/s/abcd` nesou v URL jen token; souradnice jsou az
 * za presmerovacim. Rozbaleni NESMI bezet v prohlizeci:
 *
 *   - prohlizec by sahal na cizi host zpusobem, ktery nejde vypnout v CSP
 *     uzivatele a ktery by unikal cookies jineho webu,
 *   - server muze vynutit povolene hosty, timeout a redirect limit. Bez toho
 *     je endpoint SSRF: kdokoliv posle `https://nase-api/rozbal?url=http://
 *     169.254.169.254/…` a server se popte na metadata sluzby (viz
 *     `packages/contexts/duediligence/src/internal/maps/egress.ts`, ktery
 *     dela presne tohle pro mapy).
 *
 * Pravidla jsou UZKA a stejna jako u mapoveho rendereru:
 *   - schema jen `https:`,
 *   - host PRESNE v seznamu, zadne wildcardy a zadne „konci na" — jinak by
 *     se do seznamu vecpla domena `mapy.cz.evil.example`,
 *   - zadne prihlasovaci zadani v URL,
 *   - kazdy preskok se znovu overi oproti stejnemu seznamu (jinak by
 *     allowlist platil jen na prvni hop),
 *   - timeout a limit presmerovani.
 */
/**
 * Hosti, kterym server dovoli se zeptat — kratke odkazy A adresy map, na
 * ktere ty kratke odkazy vedou. Kdyby zde byly jen kratkylinkove hosty,
 * presmerovani z `goo.gl/…` na `www.google.com/maps/@…` by skoncilo
 * odmitnutim, takhle projde a `parseMapLink` z nej vytahne souradnici.
 *
 * Seznam je UZKY a znamy: bez wildcardu a bez „konci na" (jinak by se do
 * nej vecpla domena `mapy.cz.evil.example`).
 */
export const SHORT_LINK_HOSTS = [
    ...new Set([...MAP_LINK_HOSTS, 'goo.gl', 'www.goo.gl', 'maps.app.goo.gl']),
];
/** Timeout jednoho HTTP dotazu. 5 s — krátký odkaz je rychlý, nebo neexistuje. */
export const SHORT_LINK_TIMEOUT_MS = 5_000;
/** Kolik presmerovani smi retezec mit. Tri staci i pro nejspisovanejsi sluzby. */
export const SHORT_LINK_MAX_REDIRECTS = 3;
function normalizeHosts(hosts) {
    return new Set(hosts.map((host) => host.trim().toLowerCase()).filter((host) => host !== ''));
}
function failure(reason) {
    return { kind: 'error', reason };
}
/** Overi jedno URL (puvodni i kazdy preskok) proti povolenym hostum. */
function assertAllowed(rawUrl, allowed) {
    let url;
    try {
        url = new URL(rawUrl);
    }
    catch {
        throw new ShortLinkDenied('malformed');
    }
    if (url.protocol !== 'https:')
        throw new ShortLinkDenied('protocol');
    if (url.username !== '' || url.password !== '')
        throw new ShortLinkDenied('hostNotAllowed');
    if (url.port !== '' && url.port !== '443')
        throw new ShortLinkDenied('hostNotAllowed');
    if (!allowed.has(url.hostname.toLowerCase()))
        throw new ShortLinkDenied('hostNotAllowed');
    return url;
}
class ShortLinkDenied extends Error {
    reason;
    constructor(reason) {
        super(`geo-input: kratky odkaz odmitnut (${reason})`);
        this.name = 'ShortLinkDenied';
        this.reason = reason;
    }
}
/** Je to vubec kratky odkaz (a ne treba ho znovu posilat serveru)? */
export function isShortMapLinkUrl(raw) {
    return parseMapLink(raw).kind === 'short';
}
/**
 * Rozbali kratky odkaz a vrati z nej souradnici.
 *
 * Serverova cast — nikdy se nevola z prohlizece. Po presmerovani pujde
 * vysledek znovu skrz `parseExpandedMapLink`, takze plna adresu mapy
 * rozumi stejny parser jako vlozeny odkaz.
 */
export async function resolveShortMapLink(raw, options = {}) {
    const fetchImpl = options.fetchImpl ?? fetch;
    const timeoutMs = options.timeoutMs ?? SHORT_LINK_TIMEOUT_MS;
    const maxRedirects = options.maxRedirects ?? SHORT_LINK_MAX_REDIRECTS;
    const allowed = normalizeHosts(SHORT_LINK_HOSTS);
    let current;
    try {
        current = assertAllowed(raw.trim(), allowed);
    }
    catch (error) {
        if (error instanceof ShortLinkDenied)
            return failure(error.reason);
        throw error;
    }
    for (let hop = 0; hop <= maxRedirects; hop += 1) {
        let response;
        try {
            response = await fetchImpl(current.toString(), {
                // `manual` — kazdy preskok overime sami, jinak by allowlist platil
                // jen na prvni hop (viz `egress.ts`).
                redirect: 'manual',
                signal: AbortSignal.timeout(timeoutMs),
                headers: {
                    accept: 'text/html,*/*;q=0.1',
                    'user-agent': 'REOS-map-link/1.0',
                },
            });
        }
        catch (error) {
            const name = error instanceof Error ? error.name : '';
            return failure(name === 'TimeoutError' || name === 'AbortError' ? 'timeout' : 'fetchFailed');
        }
        if (response.status >= 300 && response.status < 400) {
            const location = response.headers.get('location');
            if (location === null || location === '')
                return failure('redirectNotAllowed');
            let next;
            try {
                next = assertAllowed(new URL(location, current).toString(), allowed);
            }
            catch (error) {
                if (error instanceof ShortLinkDenied)
                    return failure(error.reason);
                throw error;
            }
            current = next;
            continue;
        }
        const finalUrl = current.toString();
        const parsed = parseMapLink(finalUrl);
        if (parsed.kind === 'point')
            return { kind: 'point', point: parsed.point, finalUrl };
        if (parsed.kind === 'error') {
            return failure(parsed.code === 'outsideCzechia' ? 'outsideCzechia' : 'notAMapLink');
        }
        // Server odpovedel, ale v adrese po presmerovani nejsou souradnice.
        return failure('notAMapLink');
    }
    return failure('tooManyRedirects');
}
/** Uzitecnost pro API: host z URL, ktery se nesmi povolit. */
export function isAllowedShortLinkHost(host) {
    return normalizeHosts(SHORT_LINK_HOSTS).has(host.trim().toLowerCase());
}
//# sourceMappingURL=short-link.js.map